/*
  hm-livraison.js — Hairmosa, source unique de la date de livraison estimée.

  Utilisé par les fiches produit (assets/hm-produit.js, assets/hm-perruque.js), par le quiz
  (assets/hm-popup-quiz.js) et par le test hors thème (outils/hm-quiz-tester.js).

  1. Calcul de la date : déplacé à l'identique depuis assets/hm-produit.js — jour calendaire à
     Paris, jours ouvrés hors samedis, dimanches et jours fériés français, format « lundi 28
     septembre ».
  2. Table des délais (jours ouvrés) par handle de produit. Un produit ABSENT de cette table
     (closures, frontales, entretien…) garde le délai réglé dans son gabarit : delai() renvoie
     alors la valeur par défaut qu'on lui passe.
  3. Ligne de statut (ligneStatut) : les produits de la table ne sont jamais « sur commande »,
     leur date s'affiche toujours ; les autres produits gardent leur comportement d'origine.
*/
(function (racine) {
  'use strict';

  /* ---------- Délais par produit ---------- */
  const DELAIS = {
    'tissage-lisse-raw-hair': 6,
    'tissage-kinky-straight': 6,
    'tissage-burmese-curl': 10,
    'clip-ins-lisses': 10,
    /* Perruques : délai majoré si la variante porte l'une de ces valeurs d'option Shopify. */
    'perruque-deep-dark-brown': { base: 8, majore: 10, options: ['L (60 cm)', 'Transparent HD'] },
    'perruque-honey-bronde-balayage': { base: 8, majore: 10, options: ['L (60 cm)', 'Transparent HD'] }
  };

  /* handle : handle Shopify du produit ; options : valeurs d'options de la variante (tableau) ;
     defaut : délai du gabarit, renvoyé tel quel si le produit n'est pas dans la table. */
  function delai(handle, options, defaut) {
    if (!Object.prototype.hasOwnProperty.call(DELAIS, handle)) return defaut;
    const regle = DELAIS[handle];
    if (typeof regle === 'number') return regle;
    const majore = (options || []).some((o) => regle.options.indexOf(o) > -1);
    return majore ? regle.majore : regle.base;
  }

  /* ---------- Calcul de la date (déplacé à l'identique depuis assets/hm-produit.js) ---------- */

  /* Aujourd'hui à Paris (Europe/Paris), en Date UTC représentant ce jour calendaire : la cliente
     voit toujours la même date quel que soit le fuseau horaire de son navigateur. */
  function parisAujourdhui() {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
    const val = (type) => Number((parts.find((p) => p.type === type) || {}).value);
    return new Date(Date.UTC(val('year'), val('month') - 1, val('day')));
  }
  const dateStr = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

  /* Dimanche de Pâques (algorithme de Meeus/Butcher), en Date UTC. */
  function paques(annee) {
    const a = annee % 19, b = Math.floor(annee / 100), c = annee % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mois = Math.floor((h + l - 7 * m + 114) / 31), jour = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(annee, mois - 1, jour));
  }

  /* Jours fériés français (fixes + mobiles calculés depuis Pâques) pour une année donnée. */
  function joursFeriesAnnee(annee) {
    const p = paques(annee);
    const decale = (n) => { const d = new Date(p); d.setUTCDate(d.getUTCDate() + n); return dateStr(d); };
    return [
      `${annee}-01-01`, `${annee}-05-01`, `${annee}-05-08`, `${annee}-07-14`,
      `${annee}-08-15`, `${annee}-11-01`, `${annee}-11-11`, `${annee}-12-25`,
      decale(1), decale(39), decale(50)
    ];
  }

  /* Ajoute n jours ouvrés (hors samedi, dimanche, jours fériés) à une date de départ, sans compter
     cette date de départ elle-même. */
  function ajouterJoursOuvres(depart, n, feries) {
    const d = new Date(depart);
    let compte = 0;
    while (compte < n) {
      d.setUTCDate(d.getUTCDate() + 1);
      const jourSemaine = d.getUTCDay();
      if (jourSemaine !== 0 && jourSemaine !== 6 && !feries.has(dateStr(d))) compte++;
    }
    return d;
  }

  /* Texte de la date de livraison (« lundi 28 septembre ») pour un délai en jours ouvrés. */
  function dateTexte(jours, loc) {
    const aujourdhui = parisAujourdhui();
    const annee = aujourdhui.getUTCFullYear();
    const feries = new Set([...joursFeriesAnnee(annee), ...joursFeriesAnnee(annee + 1)]);
    const cible = ajouterJoursOuvres(aujourdhui, Number(jours) || 0, feries);
    return cible.toLocaleDateString(loc || 'fr', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  }

  /* ---------- Ligne de statut (point de couleur + texte), commune aux fiches et au quiz ----------
     RÈGLE : un produit de la table DELAIS n'est JAMAIS « sur commande » — sa date de livraison
     s'affiche toujours. Stock > 0 : texte « en stock » (textes.livraison) ; stock ≤ 0 : même
     date, texte sans « En stock » (textes.livraisonSansStock). Tout autre produit garde le
     comportement d'origine : « sur commande » à la place de la date quand son stock est ≤ 0.

     surCommande : indicateur calculé par la fiche (vendable, stock suivi, quantité ≤ 0).
     textes : textes de la fiche (livraison, livraisonSansStock, surCommandeGras, surCommandeSuite).
     Renvoie { html, attente } — attente : point de couleur en état « attente » (stock ≤ 0). */
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  function ligneStatut(handle, surCommande, date, textes) {
    const t = textes || {};
    const dansLaTable = Object.prototype.hasOwnProperty.call(DELAIS, handle);
    if (surCommande && !dansLaTable) {
      return { html: `<b>${esc(t.surCommandeGras ?? '')}</b>${esc(t.surCommandeSuite ?? '')}`, attente: true };
    }
    const modele = surCommande ? (t.livraisonSansStock || t.livraison) : t.livraison;
    const [avant, apres] = String(modele ?? '').split('[date]');
    return { html: `${esc(avant || '')}<b>${esc(date || '')}</b>${esc(apres || '')}`, attente: !!surCommande };
  }

  const api = { delai, dateTexte, ligneStatut };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else racine.HMLivraison = api;
})(typeof window !== 'undefined' ? window : this);
